'use client';

/**
 * Thin wrapper around the Zebra BrowserPrint SDK loaded from
 * /zebra-browserprint/BrowserPrint-3.1.250.min.js. See
 * `frontend/public/zebra-browserprint/README.md` for setup.
 *
 * Both `usePrinterDiscovery` (devices feature) and `printLabels`
 * (this folder) call into here so there is one SDK loader and one
 * device-shape definition.
 */

export interface BrowserPrintDeviceRaw {
  name?: string;
  uid?: string;
  connection?: string;
  deviceType?: string;
  manufacturer?: string;
  version?: number | string;
  send?: (
    data: string,
    success?: (response: string) => void,
    failure?: (error: unknown) => void
  ) => void;
  sendThenRead?: (
    data: string,
    success: (response: string) => void,
    failure?: (error: unknown) => void
  ) => void;
}

export interface BrowserPrintStatic {
  getLocalDevices(
    success: (
      devices: BrowserPrintDeviceRaw[] | Record<string, unknown>
    ) => void,
    failure: (error: unknown) => void,
    deviceType?: string
  ): void;
}

declare global {
  interface Window {
    BrowserPrint?: BrowserPrintStatic;
  }
}

export const SCRIPT_SRC = '/zebra-browserprint/BrowserPrint-3.1.250.min.js';

// Dev-only mock — set NEXT_PUBLIC_ZEBRA_MOCK=1 in `.env.local` to bypass the
// real SDK and the local BrowserPrint Service. The mock fakes a couple of
// printers with realistic host-status responses so the discovery dialog,
// registration form and ZPL `send()` call all work end-to-end without the
// physical hardware. Keep the production build clean: this is a no-op when
// the flag is unset.
const MOCK_ENABLED = process.env.NEXT_PUBLIC_ZEBRA_MOCK === '1';

interface MockPrinterSpec {
  name: string;
  uid: string;
  connection: 'usb' | 'network';
  serial: string;
  firmware: string;
}

function buildMockPrinter(spec: MockPrinterSpec): BrowserPrintDeviceRaw {
  const hostStatus =
    `firmware version: ${spec.firmware}\r\n` +
    `serial number: ${spec.serial}\r\n` +
    `model: ${spec.name}\r\n`;
  return {
    name: spec.name,
    uid: spec.uid,
    connection: spec.connection,
    deviceType: 'printer',
    manufacturer: 'Zebra Technologies (mock)',
    version: 1,
    send: (data, success) => {
      // eslint-disable-next-line no-console
      console.info(
        `[zebra-mock] send → ${spec.name}\n` +
          `  bytes: ${data.length}\n` +
          `  full ZPL:\n${data}\n` +
          `  preview: paste the ZPL into https://labelary.com/viewer.html, ` +
          `or call previewZpl(zpl, {widthMm,heightMm,dpi}) from @/lib/zebra.`
      );
      success?.('');
    },
    sendThenRead: (_data, success) => {
      setTimeout(() => success(hostStatus), 60);
    }
  };
}

const MOCK_PRINTERS: BrowserPrintDeviceRaw[] = [
  buildMockPrinter({
    name: 'ZTC ZD420-203dpi ZPL',
    uid: 'usb-mock-zd420',
    connection: 'usb',
    serial: 'ZD420MOCK001',
    firmware: 'V77.20.10Z'
  }),
  buildMockPrinter({
    name: 'ZTC ZT411-300dpi ZPL',
    uid: '192.168.1.42',
    connection: 'network',
    serial: 'ZT411MOCK002',
    firmware: 'V79.15.22Z'
  })
];

const MOCK_BROWSERPRINT: BrowserPrintStatic = {
  getLocalDevices: (success, _failure, deviceType) => {
    setTimeout(() => {
      if (deviceType && deviceType !== 'printer') {
        success([]);
        return;
      }
      success([...MOCK_PRINTERS]);
    }, 250);
  }
};

let scriptPromise: Promise<BrowserPrintStatic> | null = null;

export function loadBrowserPrint(): Promise<BrowserPrintStatic> {
  if (MOCK_ENABLED) {
    if (!scriptPromise) scriptPromise = Promise.resolve(MOCK_BROWSERPRINT);
    return scriptPromise;
  }
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window unavailable'));
      return;
    }
    if (window.BrowserPrint) {
      resolve(window.BrowserPrint);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`
    );
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.BrowserPrint) resolve(window.BrowserPrint);
        else reject(new Error('BrowserPrint script loaded but global missing'));
      });
      existing.addEventListener('error', () =>
        reject(new Error('BrowserPrint script failed to load'))
      );
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (window.BrowserPrint) resolve(window.BrowserPrint);
      else reject(new Error('BrowserPrint global missing after load'));
    };
    script.onerror = () =>
      reject(new Error('BrowserPrint script failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function normalizeDevices(
  raw: BrowserPrintDeviceRaw[] | Record<string, unknown>
): BrowserPrintDeviceRaw[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const candidate = (raw as { printer?: BrowserPrintDeviceRaw[] }).printer;
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

export async function getLocalPrinters(): Promise<BrowserPrintDeviceRaw[]> {
  const bp = await loadBrowserPrint();
  const raw = await new Promise<
    BrowserPrintDeviceRaw[] | Record<string, unknown>
  >((resolve, reject) => {
    bp.getLocalDevices(resolve, reject, 'printer');
  });
  return normalizeDevices(raw);
}

export async function getPrinterByUid(
  uid: string
): Promise<BrowserPrintDeviceRaw | null> {
  const printers = await getLocalPrinters();
  return printers.find((p) => p.uid === uid) ?? null;
}

export function sendZpl(
  device: BrowserPrintDeviceRaw,
  zpl: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!device.send) {
      reject(new Error('Device has no send() method'));
      return;
    }
    device.send(
      zpl,
      () => resolve(),
      (err) => reject(err instanceof Error ? err : new Error(String(err)))
    );
  });
}
